// @flow
import {
  Button,
  CircularProgress,
  CssBaseline,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';

import { authInstance } from '../../firebase';

import { getAvaBackendWsUrl } from "../../utils/ws-v1";

import ScribeLoginEmailVerification from './ScribeLoginEmailVerification'

const styles = () => {
  return {
    forgotDisabled: {
      color: 'grey',
      display: 'inline-block',
    },
    forgotEnabled: {
      color: '#77B8EC',
      cursor: 'pointer',
      display: 'inline-block',
      textDecoration: 'underline',
    },
    form: {
      marginTop: 4,
      width: '100%', // Fix IE 11 issue.
    },
    paper: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginTop: 32,
      maxWidth: 420,
    },
    submit: {
      boxShadow: 'none',
      color: 'white',
      fontWeight: 600,
      margin: '12px 0 8px',
    },
    title: {
      fontWeight: 700,
    },
    inputLabel: {
      fontWeight: 400,
    }
  }
};

type Props = {
  classes: Object,
  handleNewlyLoggedInUser: Function,
  t: Function,
};

type State = {
  authError: string,
  email: string,
  emailToShow: string,
  emailInvalid: boolean,
  loading: boolean,
  password: string,
  passwordInvalid: boolean,
  resetPasswordEmailSent: boolean,
  step: number,
  usedEmails: Array<string>,
  verificationEmailSent: boolean,
  waitForEmailVerification: boolean
};

class ScribeLoginEmail extends React.Component<Props, State> {
  emailVerificationInterval;

  ws: WebSocket;

  constructor(props: Props) {
    super(props);
    this.state = {
      authError: '',
      email: '',
      emailToShow: '',
      emailInvalid: false,
      loading: false,
      password: '',
      passwordInvalid: false,
      resetPasswordEmailSent: false,
      step: 0,
      usedEmails: [],
      verificationEmailSent: false,
      waitForEmailVerification: false,
    };
  }

  async sendVerificationEmailIfNeeded() {
    const { verificationEmailSent } = this.state;
    const user = authInstance.currentUser;
    if (!verificationEmailSent && user) {
      this.setState({ loading: true });
      console.log('send email verification email');
      await user.sendEmailVerification();
      this.setState({ loading: false });
      if (this.emailVerificationInterval) {
        clearInterval(this.emailVerificationInterval);
      }
      this.emailVerificationInterval = setInterval(async () => {
        await user.reload();
        if (user.emailVerified) {
          const { handleNewlyLoggedInUser } = this.props
          if (this.emailVerificationInterval) {
            clearInterval(this.emailVerificationInterval);
          }
          this.setState({ loading: true });
          await handleNewlyLoggedInUser(user);
        }
      }, 2000);
      this.setState({
        verificationEmailSent: true,
        waitForEmailVerification: true,
      });
    }
  }

  async reset(reason) {
    this.setState({ loading: false });
    await authInstance.signOut();
    console.log('reset user - signed out', reason);
    if (this.emailVerificationInterval) {
      clearInterval(this.emailVerificationInterval);
    }
    this.setState({
      authError: undefined,
      email: undefined,
      loading: false,
      password: undefined,
      verificationEmailSent: false,
      waitForEmailVerification: false
    });
  }

  async resetPassword() {
    const { email } = this.state;
    if (this.validatePassword(email)) {
      this.setState({ loading: true });
      await authInstance.sendPasswordResetEmail(email);
      this.setState({ resetPasswordEmailSent: true, loading: false });
    }
  }

  validateEmail = email => {
    if (/^\w+([.\-+]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/.test(email)) {
      return true;
    }
    return false;
  }

  validatePassword = password => {
    return password && password.length >= 6;
  }

  disabled() {
    const { email, password } = this.state;
    return !this.validateEmail(email) || !this.validatePassword(password);
  }

  handleEmailChange(email) {
    this.setState({ emailToShow: email, email: email.toLowerCase() });
    if (this.validateEmail(email) && this.ws) {
      this.ws.send(
        JSON.stringify({
          emailEqualTo: email,
          type: "user-query",
        }),
      );
    }
  };

  async handleLoginCustomMail(event) {
    event.preventDefault();
    const { email, password } = this.state;
    this.setState({ loading: true });
    await authInstance.setPersistence('local');
    await authInstance.signInWithEmailAndPassword(email, password)
      .then(async result => {
        const { handleNewlyLoggedInUser } = this.props
        if (result.user) {
          await handleNewlyLoggedInUser(result.user);
        }
      })
      .catch(async e => {
        switch (e.code) {
          case 'auth/user-not-found':
            console.log('auth/user-not-found - try to create new user');
            await authInstance.createUserWithEmailAndPassword(
              email,
              password,
            ).then(async res => {
              console.log('new firebase user created', res);
              await this.sendVerificationEmailIfNeeded();
            });
            break;
          case 'auth/wrong-password':
            this.setState({ authError: 'wrongPassword', loading: false });
            break;
          default:
            this.setState({ loading: false });
            console.error(e);
        }
      });
  }

  handleNextStep = (e) => {
    const { email } = this.state
    e.preventDefault();
    if (this.validateEmail(email)) {
      this.setState({ step: 1, emailInvalid: false });
    } else {
      this.setState({ emailInvalid: true });
    }
  };

  initWs = () => {
    const endpoint = getAvaBackendWsUrl();
    const ws = new WebSocket(`${endpoint}/ws/users-search`);
    ws.onopen = () => {
      this.ws = ws;
    };
    ws.onmessage = (message: MessageEvent) => {
      try {
        const { usedEmails } = this.state;
        const data = JSON.parse(String(message.data));
        this.setState({
          usedEmails: [...usedEmails, ...data.users.map((u: any) => u.email)],
        });
      } catch (e) {
        console.error(e);
      }
    };
    ws.onerror = (e: any) => {
      console.error(e);
      setTimeout(() => {
        this.initWs();
      });
    };
  };

  componentDidMount() {
    this.initWs();
  }

  componentWillUnmount() {
    if (this.emailVerificationInterval) {
      clearInterval(this.emailVerificationInterval);
    }
  }

  render() {
    const { classes, t } = this.props;
    const {
      authError,
      email,
      emailToShow,
      emailInvalid,
      loading,
      password,
      passwordInvalid,
      resetPasswordEmailSent,
      step,
      usedEmails,
      waitForEmailVerification,
    } = this.state;

    const oldUser = usedEmails.includes(email || '');

    if (waitForEmailVerification) {
      return (
        <ScribeLoginEmailVerification
          email={email}
          loading={loading}
          reset={() => this.reset("userResetDuringLoader")}
        />
      );
    }
    if (resetPasswordEmailSent) {
      return (
        <Typography variant="body1" align="center">
          {t("signin-messages.resetLinkSent", { email })}
        </Typography>
      )
    }

    return (
      <div>
        <CssBaseline />
        <div className={classes.paper}>
          <Typography
            variant="h4"
            className={classes.title}
            gutterBottom
          >
            {t("signInSignUp")}
          </Typography>
          <form
            className={classes.form}
            onSubmit={(event) =>
              step === 0
                ? this.handleNextStep(event)
                : this.handleLoginCustomMail(event)
            }
            noValidate
          >
            <TextField
              variant="outlined"
              margin="normal"
              color="primary"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={emailToShow || ''}
              onChange={(event) =>
                this.handleEmailChange(event.target.value)
              }
              error={!!authError || emailInvalid}
            />
            {step === 1 && (
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoFocus
                autoComplete="current-password"
                value={password || ""}
                onChange={(event) =>
                  this.setState({ password: event.target.value })
                }
                helperText={authError && t(`signin-messages.${authError}`)}
                error={!!authError || passwordInvalid}
              />
            )}
            {step === 0 && (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={!this.validateEmail(email)}
                onClick={this.handleNextStep}
              >
                {t('continue')}
              </Button>
            )}
            {step === 1 && oldUser && (
              <Grid container>
                <Grid item xs style={{ textAlign: 'center' }}>
                  <Typography
                    onClick={() => this.resetPassword()}
                    variant="body2"
                    className={classes[
                        this.validateEmail(email)
                          ? "forgotEnabled"
                          : "forgotDisabled"
                      ]
                    }
                  >
                    {t('signin-messages.forgotPassword')}
                  </Typography>
                </Grid>
              </Grid>
            )}
            {step === 1 && (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={this.disabled() || loading}
              >
                {loading && <CircularProgress color="secondary" size="1.6em" />}
                {!loading && (oldUser ? t('signIn') : t('getStarted'))}
              </Button>
            )}
          </form>
        </div>
      </div>
    );
  }
}
export default withTranslation()(withStyles(styles)(ScribeLoginEmail));
